import { IApiCollectionItem } from "@propely/propely-typings";
import { Utilities } from "@src/utils/utilities";
import { t } from "i18next";
import { DataTableData } from "../../../../../components/deprecated/DataTable/DataTable";
import { CompanyCustomFieldData } from "../../../Company/Collections/CompanyCustomFieldData";
import { CustomFieldDataType } from "../../../Company/Collections/CustomFieldDataType";
import { CustomFieldDateDataType } from "../../../Company/Collections/CustomFieldDataTypeHelper/CustomFieldDateDataType";
import { CompanyData } from "../../../Company/CompanyData";
import { DataTypeData } from "../../../Company/DataTypeData";
import { PropertyCustomFieldData } from "../../../Property/Collections/PropertyCustomFieldData";
import { PropertyData } from "../../../Property/PropertyData";
import CollectionLoader from "../../CollectionLoader";
import { DataObjectTagField } from "../../DataObjectField/CustomFields/DeviationDataFields";
import { FirebaseDataCollectionItemFields } from "../../DataObjectField/CustomFields/FirebaseDataCollectionItemFields";
import { FirebaseDataContractorField } from "../../DataObjectField/CustomFields/FirebaseDataContractorField";
import { FirebaseDataCustomDataTypeFields } from "../../DataObjectField/CustomFields/FirebaseDataCustomDataTypeFields";
import { FirebaseDataFileDocumentField } from "../../DataObjectField/CustomFields/FirebaseDataFileDocumentField";
import { FirebaseDataNorwegianStandardField } from "../../DataObjectField/CustomFields/FirebaseDataNorwegianStandardField";
import { FirebaseDataPropertyField } from "../../DataObjectField/CustomFields/FirebaseDataPropertyField";
import { FirebaseDataUnitField } from "../../DataObjectField/CustomFields/FirebaseDataUnitField";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { FirebaseDataBooleanField } from "../../DataObjectField/Generic/FirebaseDataBooleanField";
import { FirebaseDataDateField } from "../../DataObjectField/Generic/FirebaseDataDateField";
import { FirebaseDataNumberField } from "../../DataObjectField/Generic/FirebaseDataNumberField";
import { FirebaseDataStringField } from "../../DataObjectField/Generic/FirebaseDataStringField";
import { DataObjectHelper } from "../../DataObjects/DataObjectHelper";
import { PropertyFileData } from "../../PropertyFileData";
import { GenericCollectionType } from "./CollectionItemDataHelper";
import { CompanyCollectionData } from "@src/to-be-removed/LocalData/Company/Collections/CompanyCollectionData";
import { PropertyCollectionData } from "@src/to-be-removed/LocalData/Property/Collections/PropertyCollectionData";

/**
 * @deprecated use and initialize IRawCollectionItemHelper instead
 */
export class IApiCollectionItemHelper extends DataObjectHelper<IApiCollectionItem> {
  id = () => `apiCollectionItems-${this.collection?.id}`;

  displayName = () => {
    return this.collection?.name.translate() ?? t("main.collection");
  };

  collection: GenericCollectionType | undefined;

  constructor(collection: GenericCollectionType | undefined) {
    super();
    this.collection = collection;
  }

  propertyField = new FirebaseDataPropertyField<IApiCollectionItem>(
    "property",
    t("main.property"),
    () => (this.collection?.parent instanceof PropertyData ? this.collection.parent : undefined),
    "business"
  );

  nameField = new FirebaseDataStringField<IApiCollectionItem>("name", t("main.name"), (item) => item.name, "title");

  archived = new FirebaseDataBooleanField<IApiCollectionItem>(
    "archived",
    t("main.isArchivedText"),
    (item) => item.archived ?? false,
    "archive",
    false
  );

  protected getFieldsForCollection(): DataObjectField<any, any>[] {
    const fields: DataObjectField<any, any>[] = [];
    fields.push(this.propertyField);
    fields.push(this.nameField);
    fields.push(this.archived);

    if (this.collection?.connections.propertyFiles) {
      fields.push(
        new FirebaseDataFileDocumentField<IApiCollectionItem>(
          "propertyFiles",
          t("main.propertyFiles"),
          (item) => {
            const files: PropertyFileData[] = [];
            if (this.collection?.parent instanceof PropertyData) {
              for (const fileID of item.fileIDs ?? []) {
                const file = this.collection.parent.files.addData(fileID);
                files.push(file);
              }
            }
            return files;
          },
          "folder"
        )
      );
    }
    fields.push(...(this.collection?.customFields.documents.map((field) => this.getDataObjectField(field)) ?? []));
    return fields;
  }

  allFields() {
    return this.getFieldsForCollection();
  }

  getEditFields() {
    return [this.nameField, ...(this.collection?.customFields.documents.map((field) => this.getDataObjectField(field!)) ?? [])];
  }

  getDataObjectField(field: CompanyCustomFieldData | PropertyCustomFieldData): DataObjectField<any, any> {
    switch (field.dataType) {
      case CustomFieldDataType.TEXT: {
        return new FirebaseDataStringField<IApiCollectionItem>(field.id, field.name.translate(), (item) => this.getData(item, field.id), "edit");
      }
      case CustomFieldDataType.NUMBER: {
        return new FirebaseDataNumberField<IApiCollectionItem>(field.id, field.name.translate(), (item) => this.getData(item, field.id), "pin");
      }
      case CustomFieldDataType.DATE: {
        return new FirebaseDataDateField<IApiCollectionItem>(
          field.id,
          field.name.translate(),
          (item) => {
            const value = this.getData(item, field.id);
            return CustomFieldDateDataType.getDateFieldAsDate(value, field.dataOptions);
          },
          "event"
        );
      }
      case CustomFieldDataType.TAGS: {
        return new DataObjectTagField<IApiCollectionItem>(field.id, field.name.translate(), (item) => this.getData(item, field.id), "label", true);
      }
      case CustomFieldDataType.CONTRACTORS: {
        return new FirebaseDataContractorField<IApiCollectionItem>(
          field.id,
          field.name.translate(),
          (item) => this.getData(item, field.id),
          "construction",
          true
        );
      }
      case CustomFieldDataType.NORWEGIAN_STANDARDS: {
        return new FirebaseDataNorwegianStandardField<IApiCollectionItem>(
          field.id,
          field.name.translate(),
          (data) => this.getData(data, field.id),
          "flag"
        );
      }
      case CustomFieldDataType.COLLECTION_ITEM: {
        return new FirebaseDataCollectionItemFields<IApiCollectionItem>(
          this.collection instanceof CompanyCollectionData ? this.collection.parent?.id : undefined,
          this.collection instanceof PropertyCollectionData ? this.collection.parent?.id : undefined,

          field.dataOptions.collectionID,
          field.id,
          field.name.translate(),
          (data) => this.getData(data, field.id),
          "flag"
        );
      }
      case CustomFieldDataType.CUSTOM_DATA_TYPE: {
        const collectionLoader = this.getDataTypeCollection();
        if (field.dataOptions.customDataTypeID && collectionLoader) {
          return new FirebaseDataCustomDataTypeFields<IApiCollectionItem>(
            collectionLoader.addData(field.dataOptions.customDataTypeID),
            field.id,
            field.name.translate(),
            (data) => this.getData(data, field.id),
            "flag",
            field.dataOptions.isMulti
          );
        }
        return new FirebaseDataStringField<IApiCollectionItem>(field.id, field.name.translate(), (item) => this.getData(item, field.id), "edit");
      }
      case CustomFieldDataType.TENANTS: {
        return new FirebaseDataUnitField<IApiCollectionItem>(
          field.id,
          field.name.translate(),
          (item) => this.getData(item, field.id),
          "business",
          true
        );
      }
      default: {
        return new FirebaseDataStringField<IApiCollectionItem>(field.id, field.name.translate(), (item) => this.getData(item, field.id), "edit");
      }
    }
  }

  getData(data: IApiCollectionItem, fieldID: string): any {
    return data.fields?.[fieldID]?.value?.data;
  }

  getDataTypeCollection(): CollectionLoader<DataTypeData> | undefined {
    if (this.collection?.parent instanceof PropertyData) {
      return this.collection.parent.dataTypes;
    } else if (this.collection?.parent instanceof CompanyData) {
      return this.collection.parent.dataTypes;
    }
    return undefined;
  }

  override getAllDataTableDatas(documents: IApiCollectionItem[]): DataTableData[] {
    return documents.map((item) => {
      const data: DataTableData = {
        key: item.id,
        cells: {
          name: item.name,
          visibleFor: Utilities.getReadAccessString(Utilities.getReadAccess(item.readAccess), undefined),
        },
      };

      for (const field of this.allFields()) {
        const itemData = field.dataGetter(item);
        const decodedData = field.fullDecode(itemData);

        if (field.fullIsValueValid(decodedData)) {
          data.cells[field.key] = field.getStringRepresentation(decodedData);
        } else {
          data.cells[field.key] = undefined;
        }
      }

      return data;
    });
  }
}
